/**
 * Shared, config-driven Alice suggested-action catalog and resolver.
 *
 * This is the single source of page/record-aware suggestions that every portal
 * (internal / client / sandbox) reuses, instead of hard-coding suggestions in
 * each page. Pages may still pass their own `suggestedActions` on the page
 * context to override the catalog; the resolver always filters by the portal's
 * `allowedActions` so a locked-down client portal never surfaces an internal
 * capability.
 *
 * Frontend-safe: pure data + pure functions, no fetching, no secrets.
 */

import type {
  AliceAllowedAction,
  AliceContextSourceType,
  AlicePageContext,
  AliceSuggestedAction,
  AliceSuggestedActionCategory,
  PortalAliceConfig,
} from './alice';

export const MAX_ALICE_SUGGESTIONS = 6;

interface SuggestionSeed {
  id: string;
  label: string;
  description: string;
  category: AliceSuggestedActionCategory;
  /** When true the suggestion opens a guarded Composer/draft session. */
  composer?: boolean;
  requiresApproval?: boolean;
}

function build(seed: SuggestionSeed): AliceSuggestedAction {
  return {
    id: seed.id,
    label: seed.label,
    description: seed.description,
    category: seed.category,
    action: seed.composer ? 'draft_text' : 'read_context',
    mode: seed.composer ? 'composer' : 'context',
    startsComposerSession: seed.composer ?? false,
    requiresApproval: seed.requiresApproval ?? false,
  };
}

function set(seeds: SuggestionSeed[]): AliceSuggestedAction[] {
  return seeds.map(build);
}

/** Fallback suggestions when no record is selected. */
export const GLOBAL_ALICE_SUGGESTIONS: AliceSuggestedAction[] = set([
  { id: 'global-digest', label: 'Plan my day', description: 'Draft a short operating plan from visible portal context.', category: 'summarize' },
  { id: 'global-find', label: 'Find related work', description: 'Search visible records and suggest where to look next.', category: 'navigate' },
  { id: 'global-draft', label: 'Draft a client update', description: 'Start Composer mode for a client-safe update draft.', category: 'draft', composer: true },
]);

/**
 * Suggested actions keyed by the primary record type on the page.
 * Mirrors the product spec for Tasks, Communications, Billing, Websites,
 * AI systems, Projects, Time entries, Proposals and Organizations.
 */
export const ALICE_SUGGESTION_CATALOG: Partial<
  Record<AliceContextSourceType, AliceSuggestedAction[]>
> = {
  task: set([
    { id: 'task-update', label: 'Draft client update', description: 'Turn task status and notes into a client-safe update.', category: 'draft', composer: true },
    { id: 'task-notes', label: 'Summarize latest notes', description: 'Condense the current task notes into key points.', category: 'summarize' },
    { id: 'task-process', label: 'Process notes', description: 'Extract decisions, blockers, and follow-up actions.', category: 'analyze' },
    { id: 'task-emails', label: 'Find related emails', description: 'Look for communications linked to this task.', category: 'navigate' },
    { id: 'task-follow-up', label: 'Create follow-up task', description: 'Prepare a follow-up task draft for review.', category: 'create' },
  ]),
  organization: set([
    { id: 'org-summary', label: 'Prepare account brief', description: 'Summarize relationship, work, contacts, and recent communications.', category: 'summarize' },
    { id: 'org-comms', label: 'Recent communications', description: 'Find recent emails, notes, and updates for this organization.', category: 'navigate' },
    { id: 'org-followup', label: 'Draft client check-in', description: 'Start a client check-in draft.', category: 'draft', composer: true },
    { id: 'org-risks', label: 'Find open risks', description: 'Scan visible account context for blockers and stale work.', category: 'analyze' },
  ]),
  contact: set([
    { id: 'contact-summary', label: 'Summarize contact', description: 'Who this person is, their org, and recent activity.', category: 'summarize' },
    { id: 'contact-comms', label: 'Recent communications', description: 'Find emails and notes involving this contact.', category: 'navigate' },
    { id: 'contact-draft', label: 'Draft email', description: 'Draft an email to this contact.', category: 'draft', composer: true },
  ]),
  lead: set([
    { id: 'lead-summary', label: 'Summarize lead', description: 'Summarize this prospect and where they are in the pipeline.', category: 'summarize' },
    { id: 'lead-followup', label: 'Draft follow-up', description: 'Draft a follow-up message to this lead.', category: 'draft', composer: true },
    { id: 'lead-proposal', label: 'Outline a proposal', description: 'Draft a proposal outline for this lead.', category: 'draft', composer: true },
  ]),
  communication: set([
    { id: 'comm-reply', label: 'Draft reply', description: 'Start a reply draft from this thread.', category: 'draft', composer: true },
    { id: 'comm-actions', label: 'Pull action items', description: 'Extract commitments, blockers, and follow-ups.', category: 'analyze' },
    { id: 'comm-task', label: 'Update related task', description: 'Suggest task updates from this communication.', category: 'update' },
    { id: 'comm-summary', label: 'Summarize thread', description: 'Summarize the current communication thread.', category: 'summarize' },
  ]),
  email_thread: set([
    { id: 'thread-reply', label: 'Draft reply', description: 'Start a reply draft from this email thread.', category: 'draft', composer: true },
    { id: 'thread-summary', label: 'Summarize thread', description: 'Summarize the current email thread.', category: 'summarize' },
    { id: 'thread-actions', label: 'Pull action items', description: 'Extract commitments and follow-ups from this thread.', category: 'analyze' },
  ]),
  website: set([
    { id: 'web-update', label: 'Draft client update', description: 'Draft a client-safe update about this website.', category: 'draft', composer: true },
    { id: 'web-status', label: 'Summarize site status', description: 'Summarize hosting, status, and recent activity.', category: 'summarize' },
    { id: 'web-content', label: 'Create content task', description: 'Prepare a content or maintenance task draft.', category: 'create' },
    { id: 'web-changes', label: 'Review recent changes', description: 'Scan visible context for recent site changes.', category: 'analyze' },
  ]),
  project: set([
    { id: 'project-status', label: 'Summarize project status', description: 'Summarize progress, child tasks, and blockers.', category: 'summarize' },
    { id: 'project-update', label: 'Draft client update', description: 'Draft a client-safe project status update.', category: 'draft', composer: true },
    { id: 'project-risks', label: 'Find open risks', description: 'Identify stale or blocked work in this project.', category: 'analyze' },
  ]),
  invoice: set([
    { id: 'invoice-reminder', label: 'Draft payment reminder', description: 'Prepare a friendly payment reminder.', category: 'draft', composer: true, requiresApproval: true },
    { id: 'invoice-explain', label: 'Explain invoice', description: 'Summarize invoice status and amount details.', category: 'summarize' },
    { id: 'invoice-history', label: 'Check payment history', description: 'Review visible billing context for recent payment activity.', category: 'analyze' },
    { id: 'invoice-summary', label: 'Prepare billing summary', description: 'Draft a billing summary for internal review.', category: 'summarize' },
  ]),
  subscription: set([
    { id: 'subscription-summary', label: 'Summarize subscription', description: 'Explain recurring billing state and renewal timing.', category: 'summarize' },
    { id: 'subscription-client', label: 'Draft subscription note', description: 'Prepare a client-facing subscription note.', category: 'draft', composer: true },
  ]),
  payment: set([
    { id: 'payment-summary', label: 'Prepare payment summary', description: 'Summarize payment status, platform, and reconciliation context.', category: 'summarize' },
    { id: 'payment-invoice', label: 'Explain invoice link', description: 'Describe how this payment appears to relate to billing records.', category: 'analyze' },
  ]),
  time_entry: set([
    { id: 'time-summary', label: 'Summarize tracked time', description: 'Summarize the visible time entry and its task.', category: 'summarize' },
    { id: 'time-billing', label: 'Prepare billing note', description: 'Draft an internal note about this tracked time.', category: 'draft', composer: true },
  ]),
  form: set([
    { id: 'form-summary', label: 'Summarize form', description: 'Summarize the visible form setup and recent submission context.', category: 'summarize' },
    { id: 'form-entries', label: 'Check entries', description: 'Look up related form entries and submission counts.', category: 'analyze' },
  ]),
  form_entry: set([
    { id: 'form-entry-summary', label: 'Summarize entry', description: 'Summarize the selected form entry and submitted fields.', category: 'summarize' },
    { id: 'form-entry-follow-up', label: 'Draft follow-up', description: 'Draft a client-safe follow-up from the selected entry.', category: 'draft', composer: true },
  ]),
  agent: set([
    { id: 'agent-summary', label: 'Summarize agent', description: 'Explain agent type, status, visibility, and connection state.', category: 'summarize' },
    { id: 'agent-performance', label: 'Review agent performance', description: 'Review visible performance and connection signals.', category: 'analyze' },
    { id: 'agent-checklist', label: 'Check missing details', description: 'List missing profile or connection fields.', category: 'analyze' },
    { id: 'agent-prompts', label: 'Show related prompts', description: 'Surface prompts related to this agent.', category: 'navigate' },
  ]),
  call: set([
    { id: 'call-summary', label: 'Summarize call', description: 'Condense caller, outcome, reason, and follow-up context.', category: 'summarize' },
    { id: 'call-actions', label: 'Pull follow-ups', description: 'Extract follow-up actions from visible call fields.', category: 'analyze' },
  ]),
  prompt: set([
    { id: 'prompt-tighten', label: 'Tighten prompt', description: 'Suggest clearer wording and guardrails.', category: 'draft', composer: true },
    { id: 'prompt-missing', label: 'Check missing details', description: 'List missing purpose, type, and version details.', category: 'analyze' },
  ]),
  proposal: set([
    { id: 'proposal-scope', label: 'Tighten scope', description: 'Improve scope clarity and acceptance language.', category: 'draft', composer: true },
    { id: 'proposal-persuasive', label: 'Make it more persuasive', description: 'Draft a more persuasive proposal section.', category: 'draft', composer: true },
    { id: 'proposal-client', label: 'Create simple client version', description: 'Simplify proposal language for client review.', category: 'draft', composer: true },
    { id: 'proposal-missing', label: 'Check missing details', description: 'Find unclear or incomplete proposal sections.', category: 'analyze' },
  ]),
};

/**
 * Resolves the suggestions Alice should show for the current context, scoped by
 * the portal config. Order of precedence:
 *   1. Suggestions explicitly attached to the page context (page override).
 *   2. Catalog suggestions for the primary record type.
 *   3. Global fallback suggestions.
 * Everything is filtered by the portal's `allowedActions`.
 */
export function resolveAliceSuggestions(
  context: AlicePageContext,
  config: PortalAliceConfig,
): AliceSuggestedAction[] {
  if (!config.quickActionsEnabled) return [];
  if (config.suggestedActionsEnabled === false) return [];

  const allowed = new Set<AliceAllowedAction>(config.allowedActions);
  const isAllowed = (item: AliceSuggestedAction) => allowed.has(item.action);

  const fromPage = context.suggestedActions.filter(isAllowed);
  if (fromPage.length > 0) return fromPage.slice(0, MAX_ALICE_SUGGESTIONS);

  const fromCatalog = context.recordType
    ? ALICE_SUGGESTION_CATALOG[context.recordType]
    : undefined;
  const base = fromCatalog ?? GLOBAL_ALICE_SUGGESTIONS;
  return base.filter(isAllowed).slice(0, MAX_ALICE_SUGGESTIONS);
}
